import type { QueryClient } from "@tanstack/react-query"
import { amplifierKeys } from "../hooks/queryKeys"
import type { Amplifier, AntennaAngle, Station } from "@/types"

export type AmplifierSsePayload = Partial<
  Pick<
    Amplifier,
    | "id"
    | "start_frequency"
    | "end_frequency"
    | "bandwidth"
    | "modulation"
    | "sf"
    | "chirp"
    | "cbw_khz"
    | "timer"
    | "sweep_time"
    | "custom_sweeps"
    | "sweeps"
    | "polarization_angle"
    | "freq_input_mode"
  >
> & {
  // backward-compat / optional fields
  amplifierId?: string
  last_preset_id?: string | null
}

function areSweepsEqual(
  a: Amplifier["sweeps"] | undefined,
  b: Amplifier["sweeps"] | undefined,
) {
  if (a === b) return true
  if (!a || !b) return false
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const [a0, a1] = a[i] ?? []
    const [b0, b1] = b[i] ?? []
    if (a0 !== b0 || a1 !== b1) return false
  }
  return true
}

function findAmplifierInStationCache(
  station: Station | undefined,
  amplifierId: string,
) {
  const units = station?.units ?? []
  for (const unit of units) {
    const amplifiers = unit.amplifiers ?? []
    for (const amp of amplifiers) {
      if (amp.id === amplifierId) return amp
    }
  }
  return undefined
}

function findAmplifierInAnyStationCache(
  queryClient: QueryClient,
  amplifierId: string,
) {
  const stations = queryClient.getQueriesData<Station>({
    queryKey: ["station"],
  })
  for (const [, station] of stations) {
    const amp = findAmplifierInStationCache(station, amplifierId)
    if (amp) return amp
  }
  return undefined
}

function applyAmplifierSseUpdate(
  prev: Amplifier,
  data: AmplifierSsePayload,
): Amplifier {
  let changed = false
  const patch: Partial<Amplifier> = {}

  // Only update the "state-like" fields we care about. Preserve config + bandwidth_options, etc.
  if (
    "start_frequency" in data &&
    data.start_frequency !== undefined &&
    data.start_frequency !== prev.start_frequency
  ) {
    patch.start_frequency = data.start_frequency as number
    changed = true
  }
  if (
    "end_frequency" in data &&
    data.end_frequency !== undefined &&
    data.end_frequency !== prev.end_frequency
  ) {
    patch.end_frequency = data.end_frequency as number
    changed = true
  }
  if (
    "bandwidth" in data &&
    data.bandwidth !== undefined &&
    data.bandwidth !== prev.bandwidth
  ) {
    patch.bandwidth = data.bandwidth as number
    changed = true
  }
  if (
    "modulation" in data &&
    data.modulation !== undefined &&
    data.modulation !== prev.modulation
  ) {
    patch.modulation = data.modulation as string
    changed = true
  }
  if ("sf" in data && data.sf !== undefined && data.sf !== prev.sf) {
    patch.sf = data.sf as number | null
    changed = true
  }
  if (
    "chirp" in data &&
    data.chirp !== undefined &&
    data.chirp !== prev.chirp
  ) {
    patch.chirp = data.chirp as string | null
    changed = true
  }
  if (
    "cbw_khz" in data &&
    data.cbw_khz !== undefined &&
    data.cbw_khz !== prev.cbw_khz
  ) {
    patch.cbw_khz = data.cbw_khz as number | null
    changed = true
  }
  if (
    "timer" in data &&
    data.timer !== undefined &&
    data.timer !== prev.timer
  ) {
    patch.timer = data.timer as number
    changed = true
  }
  if (
    "sweep_time" in data &&
    data.sweep_time !== undefined &&
    data.sweep_time !== prev.sweep_time
  ) {
    patch.sweep_time = data.sweep_time as number
    changed = true
  }
  if (
    "custom_sweeps" in data &&
    data.custom_sweeps !== undefined &&
    data.custom_sweeps !== prev.custom_sweeps
  ) {
    patch.custom_sweeps = data.custom_sweeps as boolean
    changed = true
  }
  if (
    "polarization_angle" in data &&
    data.polarization_angle !== undefined &&
    data.polarization_angle !== prev.polarization_angle
  ) {
    patch.polarization_angle = data.polarization_angle as AntennaAngle
    changed = true
  }
  if ("sweeps" in data) {
    const nextSweeps = data.sweeps as Amplifier["sweeps"] | undefined
    if (!areSweepsEqual(prev.sweeps, nextSweeps)) {
      patch.sweeps = nextSweeps as any
      changed = true
    }
  }
  if ("freq_input_mode" in data) {
    patch.freq_input_mode = data.freq_input_mode as Amplifier["freq_input_mode"]
    changed = true
  }

  let nextLastPreset = prev.last_preset
  if ("last_preset_id" in data) {
    const prevPresetId = prev.last_preset ? prev.last_preset.id : null
    const nextPresetIdRaw = data.last_preset_id ?? null
    const nextPresetId = nextPresetIdRaw ? String(nextPresetIdRaw) : null
    if (prevPresetId !== nextPresetId) {
      changed = true
      nextLastPreset = nextPresetId ? ({ id: nextPresetId } as any) : undefined
    }
  }

  if (!changed) return prev
  return { ...prev, ...patch, last_preset: nextLastPreset }
}

export function patchAmplifierFromSse(
  queryClient: QueryClient,
  amplifierId: string,
  data: AmplifierSsePayload,
) {
  const amplifierFromStation = findAmplifierInAnyStationCache(
    queryClient,
    amplifierId,
  )

  queryClient.setQueryData<Amplifier>(
    amplifierKeys.byId(amplifierId),
    (old) => {
      const base = old ?? amplifierFromStation
      if (!base) return old
      return applyAmplifierSseUpdate(base, data)
    },
  )

  // Patch all cached station queries so station-based views stay consistent.
  queryClient.setQueriesData<Station>({ queryKey: ["station"] }, (old) => {
    if (!old?.units) return old

    let didChange = false
    const nextUnits = old.units.map((unit) => {
      const amps = unit.amplifiers ?? []
      let unitChanged = false

      const nextAmps = amps.map((amp) => {
        if (amp.id !== amplifierId) return amp
        const nextAmp = applyAmplifierSseUpdate(amp, data)
        if (nextAmp !== amp) unitChanged = true
        return nextAmp
      })

      if (!unitChanged) return unit
      didChange = true
      return { ...unit, amplifiers: nextAmps }
    })

    if (!didChange) return old
    return { ...old, units: nextUnits }
  })
}
